/**
 * Contacts live adapter - pure request/response mapping. No Supabase import and
 * no IO, so these helpers are unit-testable in a Node test environment (the
 * adapter class that performs the invokes lives in liveAdapter.ts and imports
 * from here). Covers the live response row shape, temperature precedence, and
 * the partial-update body builder.
 */

import type {
  ContactPatch,
  ContactTemperature,
  ContactTemperatureSource,
} from "../types";

export interface LiveMatchedHeadline {
  signal_id: string;
  headline: string;
  lifecycle_status: string | null;
  scf_score: number | null;
  tier: number | null;
  observed_at: string | null;
  /** Source article URL (Codex, in flight); null/absent until it deploys. */
  source_url?: string | null;
}

export interface LiveContactRow {
  id: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  organization_id: string | null;
  organization_name?: string | null;
  /** present on import-insert rows */
  company_name?: string | null;
  title: string | null;
  notes: string | null;
  owner_user_id: string | null;
  uploaded_by_user_id: string | null;
  /** Origin marker (Codex, d74ffc1): 'manual' | 'uploaded'. Backs the source tag. */
  contact_origin?: string | null;
  signal_match_count?: number | null;
  matched_headlines?: LiveMatchedHeadline[] | null;
  /** Temperature landed as a real column; source says whether the user set it. */
  temperature?: string | null;
  temperature_source?: string | null;
  /** Firm-wide derived boolean (list_contacts v15): Active Delivery Graph
   *  engagement at the contact's canonical organization. */
  active_engagement?: boolean | null;
  /** Event/source group ("Web Summit 2025"). Backs the met-at line + pills. */
  event_source?: string | null;
  /** LinkedIn URL. `linkedin_url` is the older field name; both are read. */
  linkedin?: string | null;
  linkedin_url?: string | null;
  date_met?: string | null;
  last_contact_date?: string | null;
  contact_count?: number | null;
  reminder_date?: string | null;
  reminder_time?: string | null;
  reminder_reason?: string | null;
  /** Retained for older seeds that only exposed the follow-up event surface. */
  next_follow_up_due_at?: string | null;
  latest_follow_up_event?: unknown;
  owner?: { user_id: string; display_name: string | null; email: string | null } | null;
}

/** Non-empty trimmed text, else null (a present null clears the column). */
export function textOrNull(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t.length ? t : null;
}

const TEMPERATURES = new Set<ContactTemperature>(["hot", "warm", "cold"]);
function isTemperature(v: unknown): v is ContactTemperature {
  return typeof v === "string" && TEMPERATURES.has(v as ContactTemperature);
}

/**
 * Resolve the displayed temperature and its provenance. The server-provided
 * `temperature` is authoritative: a valid value is shown (with `temperature_source`
 * saying stored vs. server-derived), and a null/absent value is UNSET — never
 * inferred client-side from signal score. `fallback` is only the user's own
 * just-submitted value on an add/update response that does not echo the column
 * (an optimistic passthrough, not signal inference); without it, unset is null.
 */
export function resolveTemperature(
  row: Pick<LiveContactRow, "temperature" | "temperature_source">,
  fallback?: ContactTemperature,
): { temperature: ContactTemperature | null; temperatureSource: ContactTemperatureSource } {
  if (isTemperature(row.temperature)) {
    return {
      temperature: row.temperature,
      temperatureSource: row.temperature_source === "stored" ? "stored" : "signal_strength",
    };
  }
  if (fallback) {
    return { temperature: fallback, temperatureSource: "stored" };
  }
  return { temperature: null, temperatureSource: "stored" };
}

/**
 * Build the update_contact body from a partial patch. Only present keys are
 * added, so an unchanged field is never sent (and so never accidentally
 * cleared). Mirrors the Edge Function's `if (body.key !== undefined)` convention.
 */
export function buildUpdateBody(id: string, patch: ContactPatch): Record<string, unknown> {
  const body: Record<string, unknown> = { contact_id: id };
  if (patch.name !== undefined) body.display_name = patch.name;
  if (patch.title !== undefined) body.title = textOrNull(patch.title);
  if (patch.company !== undefined) body.company = textOrNull(patch.company);
  if (patch.email !== undefined) body.email = textOrNull(patch.email);
  if (patch.phone !== undefined) body.phone = textOrNull(patch.phone);
  if (patch.notes !== undefined) body.notes = textOrNull(patch.notes);
  if (patch.linkedin !== undefined) body.linkedin = textOrNull(patch.linkedin);
  if (patch.eventSource !== undefined) body.event_source = textOrNull(patch.eventSource);
  if (patch.dateMet !== undefined) body.date_met = textOrNull(patch.dateMet);
  if (patch.temperature !== undefined) body.temperature = patch.temperature;
  if (patch.reminderDate !== undefined) body.reminder_date = textOrNull(patch.reminderDate);
  if (patch.reminderTime !== undefined) body.reminder_time = textOrNull(patch.reminderTime);
  if (patch.reminderReason !== undefined) body.reminder_reason = textOrNull(patch.reminderReason);
  return body;
}
